import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Divider, Form, Row } from 'antd'
import { includes, isNil, isUndefined, keys, reduce } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'
import { getFilterOptions } from '../../../core/utils'

import {
  FormButtons,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import { StyledPaddingTopCol } from '../../../common/components/styled'
import { PERMISSIONS, ROLES } from '../../../common/constants'
import { useFormDirty } from '../../../common/hooks'

import { useUserHasPermissions, useUserHasRoles } from '../../../account/hooks'
import {
  MENU_CURATION_TASK_PROBLEM_REASON,
  MENU_CURATION_TASK_TAGS,
} from '../../constants'
import { menuCurationTaskPropType } from '../../prop-types'
import {
  MenuCurationTaskScrapingReviewButtons,
  ScrapingReviewCommentsModal,
} from '../sections'

import { menuCurationTaskTagsAndProblemsFormInitialValues } from './initial-values'

const MenuCurationTaskTagsAndProblemsForm = ({
  menuCurationTask,
  isLoading,
  isDisabled,
  onSubmit,
}) => {
  const [selectedTags, setSelectedTags] = useState(null)
  const [selectedProblemReason, setSelectedProblemReason] = useState(null)
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const initialValues = useMemo(
    () => menuCurationTaskTagsAndProblemsFormInitialValues(menuCurationTask),
    [menuCurationTask],
  )

  const hasPermissionToChangeTags = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UPDATE_TAGS,
  )

  useEffect(() => {
    resetForm()
    setSelectedTags(initialValues.tags)
    setSelectedProblemReason(initialValues.problemReason)
  }, [initialValues, resetForm, setSelectedTags])

  const isDuplicateTask = useMemo(
    () => !isNil(menuCurationTask.menu?.originalMenuId),
    [menuCurationTask.menu?.originalMenuId],
  )

  const areDuplicateTaskTagsDisabled = useUserHasRoles([
    ROLES.TRAINEE.value,
    ROLES.CURATOR.value,
    ROLES.QA_TRAINEE.value,
  ])

  const tagsOptions = useMemo(
    () =>
      getFilterOptions(
        reduce(
          keys(MENU_CURATION_TASK_TAGS),
          (acc, tag) => {
            if (!isDuplicateTask) {
              return {
                ...acc,
                [tag]: MENU_CURATION_TASK_TAGS[tag],
              }
            }

            if (
              MENU_CURATION_TASK_TAGS[tag].isOnlyForDuplicateTasks &&
              !areDuplicateTaskTagsDisabled
            ) {
              return {
                ...acc,
                [tag]: { ...MENU_CURATION_TASK_TAGS[tag], disabled: false },
              }
            }

            return {
              ...acc,
              [tag]: MENU_CURATION_TASK_TAGS[tag],
            }
          },
          [],
        ),
        'label',
        'value',
      ),
    [areDuplicateTaskTagsDisabled, isDuplicateTask],
  )
  const problemReasonOptions = useMemo(
    () => getFilterOptions(MENU_CURATION_TASK_PROBLEM_REASON, 'label', 'value'),
    [],
  )

  const areTagsDisabled = useMemo(
    () => isDisabled || !hasPermissionToChangeTags,
    [hasPermissionToChangeTags, isDisabled],
  )

  const isProblemReasonDisabled = useMemo(
    () =>
      isDisabled ||
      !includes(selectedTags, MENU_CURATION_TASK_TAGS.IS_PROBLEMATIC.value),
    [isDisabled, selectedTags],
  )

  const isProblemCommentDisabled = useMemo(
    () => isDisabled || isProblemReasonDisabled || isNil(selectedProblemReason),
    [isDisabled, isProblemReasonDisabled, selectedProblemReason],
  )

  const handleFormValuesChange = useCallback(changedValues => {
    if (!isUndefined(changedValues.tags)) {
      setSelectedTags(changedValues.tags)
    }
    if (!isUndefined(changedValues.problemReason)) {
      setSelectedProblemReason(changedValues.problemReason)
    }
  }, [])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="editMenuCurationTask"
      onFinish={onSubmit}
      autoComplete="off"
      onFieldsChange={setToDirty}
      onValuesChange={handleFormValuesChange}
    >
      <Row gutter={[16]}>
        <Col span={5}>
          <FormSelect
            mode="multiple"
            label="Tags"
            labelAbove
            showSearch
            name="tags"
            options={tagsOptions}
            placeholder="Select tags..."
            disabled={areTagsDisabled}
            showColon={false}
          />
        </Col>
        <Col span={3}>
          <FormSelect
            label="Problem reason"
            labelAbove
            showSearch
            allowClear
            name="problemReason"
            rules={[
              {
                required: !isProblemReasonDisabled,
                message: 'Please select a problem reason',
              },
            ]}
            options={problemReasonOptions}
            placeholder="Select a problem reason..."
            disabled={isProblemReasonDisabled}
            showColon={false}
          />
        </Col>
        <Col span={5}>
          <FormTextareaInput
            label="Problem comment"
            labelAbove
            name="problemComment"
            rules={[
              {
                required: !isProblemCommentDisabled,
                message: 'Please select a problem comment',
              },
            ]}
            placeholder="Select a problem comment..."
            disabled={isProblemCommentDisabled}
            showColon={false}
          />
        </Col>
        <GridFormItem span={6} label="Scraping review">
          <MenuCurationTaskScrapingReviewButtons
            taskStatus={menuCurationTask.taskStatus}
            scrapingReviewComment={menuCurationTask.scrapingReviewComment}
            scrapingClarificationComment={
              menuCurationTask.scrapingClarificationComment
            }
            scrapingReviewerComment={menuCurationTask.scrapingReviewerComment}
            isScrapingReviewDone={menuCurationTask.isScrapingReviewDone}
          />
          <ScrapingReviewCommentsModal
            menuCurationTaskId={menuCurationTask.id}
          />
        </GridFormItem>
        <StyledPaddingTopCol span={4}>
          <FormButtons
            permissions={[PERMISSIONS.MENU_CURATION_TASK.UPDATE_TAGS]}
            isDisabled={!isDirty}
            isLoading={isLoading}
            onReset={resetForm}
          />
        </StyledPaddingTopCol>
      </Row>
      <Divider />
    </Form>
  )
}

MenuCurationTaskTagsAndProblemsForm.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

MenuCurationTaskTagsAndProblemsForm.defaultProps = {
  isLoading: false,
  isDisabled: false,
}

export default MenuCurationTaskTagsAndProblemsForm
